<!-- src/views/eventos/EditEvento/ConfiguracionesGenerales/AccesoPublicoYCorreo/AccesoPublicoYCorreo.vue -->
<template>
  <div class="space-y-6 formulario">
    <!-- Switch permitir ingreso público -->
    <div class="flex items-center justify-between p-4 rounded-lg border border-gray-200">
      <div class="mr-4">
        <div class="font-semibold text-gray-800">permitir ingreso publico de empresas</div>
        <div class="text-sm text-gray-500">Habilita el registro público por enlace.</div>
      </div>

      <button
        type="button"
        class="relative inline-flex h-6 w-11 items-center rounded-full transition"
        :class="enabled ? 'bg-green-500' : 'bg-gray-300'"
        @click="togglePublico"
      >
        <span
          class="inline-block h-5 w-5 transform bg-white rounded-full transition"
          :class="enabled ? 'translate-x-5' : 'translate-x-1'"
        />
      </button>
    </div>

    <!-- Nota “guardar” -->
    <div
      v-if="enabled && showSaveNote"
      class="p-3 rounded bg-amber-50 border border-amber-200 text-amber-800 text-sm"
    >
      Has habilitado el registro público. <strong>Guarda cambios</strong> para generar y ver el enlace.
    </div>

    <!-- Link -->
    <div v-if="enabled && showLink" class="p-4 rounded-lg bg-gray-50 border border-dashed border-gray-300">
      <div class="font-medium mb-1">Link de registro</div>
      <div class="text-sm">
        <code class="px-2 py-1 bg-white rounded border">{{ registroLink }}</code>
      </div>
    </div>

    <!-- Campos de correo -->
    <div class="rounded-lg border border-gray-200 p-5">
        <div class="w-full mb-5">
            <h2><b>Configuracion para mails personalizados</b></h2>
        </div>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 ">
        
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Mail custom</label>
        <input
          type="text"
          class="mt-1 w-full border-gray-300 rounded-md shadow-sm"
          :value="evento.mail_custom ?? ''"
          @input="emit('patch-evento', { mail_custom: $event.target.value })"
        />
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Password mail custom</label>
        <input
          type="password"
          class="mt-1 w-full border-gray-300 rounded-md shadow-sm"
          :value="evento.pass_mail_custom ?? ''"
          @input="emit('patch-evento', { pass_mail_custom: $event.target.value })"
        />
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Server mail custom</label>
        <input
          type="text"
          class="mt-1 w-full border-gray-300 rounded-md shadow-sm"
          :value="evento.server_mail_custom ?? ''"
          @input="emit('patch-evento', { server_mail_custom: $event.target.value })"
        />
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Tipo SMTP (type_smpt)</label>
        <select
          class="mt-1 w-full border-gray-300 rounded-md shadow-sm"
          :value="evento.type_smpt ?? ''"
          @change="emit('patch-evento', { type_smpt: $event.target.value })"
        >
          <option value="">Selecciona…</option>
          <option value="smtp">smtp</option>
          <option value="api">api</option>
          <option value="otro">otro</option>
        </select>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Seguridad (type_security)</label>
        <select
          class="mt-1 w-full border-gray-300 rounded-md shadow-sm"
          :value="evento.type_security ?? ''"
          @change="emit('patch-evento', { type_security: $event.target.value })"
        >
          <option value="">sin seguridad</option>
          <option value="ssl">ssl</option>
          <option value="tls">tls</option>
          <option value="starttls">starttls</option>
        </select>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Puerto</label>
        <input
          type="text"
          inputmode="numeric"
          maxlength="4"
          class="mt-1 w-full border-gray-300 rounded-md shadow-sm"
          :value="evento.port ?? ''"
          @input="emit('patch-evento', { port: $event.target.value })"
        />
      </div>
    </div>
    </div>

    <!-- Botón de prueba -->
    <div class="flex justify-end">
      <button
        type="button"
        class="px-4 py-2 rounded bg-gray-800 text-white font-semibold hover:bg-gray-700 disabled:opacity-50"
        :disabled="testing"
        @click="onTestClick"
      >
        {{ testing ? 'Probando…' : 'Probar envío' }}
      </button>
    </div>
  </div>
</template>

<script setup lang="js">
import { defineProps, defineEmits, computed, ref, watch } from 'vue'
  import { useStore } from 'vuex';

import { useToast } from 'vue-toastification'
const toast = useToast()
const props = defineProps({
  evento:  { type: Object, required: true },
  savedAt: { type: Number, default: 0 },
  // Estos dos props son necesarios para probar envío desde el hijo:
  baseUrl:   { type: String, required: true },
  userToken: { type: String, required: true } // token del usuario destino
})
const emit = defineEmits(['patch-evento', 'toast']) // 'toast' (message, type) opcional si quieres reusar toasts del padre

/* Normaliza 0/1/'0'/'1'/true/false a boolean */
function asBool(v) {
  return v === true || v === 1 || v === '1'
}

/* Estado derivado de props */
const enabled         = computed(() => asBool(props.evento?.permitir_ingreso_publico))
const hasLocalToggle  = ref(false)
const initialEnabled  = ref(asBool(props.evento?.permitir_ingreso_publico))

watch(() => props.evento?.permitir_ingreso_publico, (nv) => {
  if (!hasLocalToggle.value) {
    initialEnabled.value = asBool(nv)
  }
})

watch(() => props.savedAt, () => {
  if (enabled.value) {
    initialEnabled.value = true
    hasLocalToggle.value = false
  }
})

const registroLink  = computed(() => {
  const origin = typeof window !== 'undefined' && window.location ? window.location.origin : ''
  const token  = props.evento?.token ?? ''
  return `${origin}/registro-control/${token}`
})

const showLink     = computed(() => enabled.value && initialEnabled.value)
const showSaveNote = computed(() => enabled.value && !initialEnabled.value && hasLocalToggle.value)

/* Toggle */
function togglePublico() {
  const next = !enabled.value
  hasLocalToggle.value = true
  emit('patch-evento', { permitir_ingreso_publico: next })
}

const store = useStore();

/* Probar envío */
const testing = ref(false)
async function onTestClick() {
  // Validación mínima
  const e = props.evento || {}
    const token = store.state.user.token;
  if (!token) {
     toast.error('No hay token de usuario para enviar prueba.')
     
     return  
    }
if (!e.mail_custom || !e.pass_mail_custom || !e.server_mail_custom) {
      toast.error('Completa servidor, usuario y contraseña SMTP antes de probar.')
    return 
  }

  testing.value = true
  try {
    const res = await fetch(`/api/eventos/${e.token}/mail/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_token:        token,
        mail_custom:       e.mail_custom,
        pass_mail_custom:  e.pass_mail_custom,
        server_mail_custom:e.server_mail_custom,
        type_smpt:         e.type_smpt || 'smtp',
        type_security:     e.type_security || '',
        port:              e.port || ''
      })
    })
    const data = await res.json()
    if (data?.status === 'ok') {
      toast.success('Correo de prueba enviado.')
    } else {
        toast.error('No se pudo enviar el correo de prueba.')
    }
} catch (err) {
    toast.error('Error al probar envío: ' + (err?.message || err))
  } finally {
    testing.value = false
  }
}
</script>
<style scoped>
.formulario {
    width: 80vw;
    max-width: 80%;
    margin: 0 auto;
}

</style>